import React, { useMemo } from "react";
import styled from "styled-components";
import { motion, useSpring, useTransform } from "framer-motion";
import { experiences } from "../../data/constants";
import { sealIn, staggerContainer, viewport } from "../../utils/motion";

// SVG elements scale from the user-space origin (0,0) by default, which would
// fling these groups in from the map's top-left corner. `fill-box` + `center`
// makes each group scale around itself, so they pop in place.
const POP = { transformBox: "fill-box", transformOrigin: "center" };

// Scales the clip rect down the Y axis so the dashed trail is uncovered
// progressively, as if it were being walked.
const trailWipe = {
  hidden: { scaleY: 0 },
  show: { scaleY: 1, transition: { duration: 1.3, ease: "easeInOut" } },
};

// "Campsites along the trail" — the objective's illustrated map, rebuilt as
// inline SVG so it obeys the design contract: flat fills, 1px strokes, no
// gradients and no soft shadows.
//
// Nodes are laid out down a serpentine path, newest at the top; they read as
// airports. A plane flies the route between them while clouds drift and birds
// cross the sky behind.

// Flat-fill airliner seen from above, nose pointing +X so `offsetRotate: auto`
// can steer it along the route. Cream body against the tan map so it reads at
// small size; hard edges only, no gradient and no shadow.
//
// Wings and tailplanes are drawn first, fuselage last, so the body sits over
// the join and the whole thing reads as one silhouette.
// Single-outline airliner (Material "flight" glyph, Apache-2.0). Hand-rolled
// wings kept reading as arrow fletching at this size; one solid silhouette is
// unmistakable. The glyph points up, so it is rotated 90° to face +X for
// `offsetRotate: auto`, then centred on its own origin.
const PLANE_D =
  "M21 16v-2l-8-5V3.5c0-.83-.67-1.5-1.5-1.5S10 2.67 10 3.5V9l-8 5v2l8-2.5V19" +
  "l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5l8 2.5z";

const Plane = () => (
  <g transform="scale(1.65) rotate(90) translate(-12, -12)">
    <path d={PLANE_D} fill="#f7ecd8" stroke="#2f2119" strokeWidth="1.1" />
  </g>
);

// Two-stroke gull silhouette, drawn small. Flaps by scaling on Y.
const Bird = ({ x, y, s = 1, delay = 0 }) => (
  <motion.path
    d="M0 4 Q4 0 8 4 Q12 0 16 4"
    fill="none"
    stroke="#6c5b47"
    strokeWidth="1.4"
    transform={`translate(${x}, ${y}) scale(${s})`}
    style={{ transformBox: "fill-box", transformOrigin: "center" }}
    animate={{ scaleY: [1, 0.55, 1] }}
    transition={{
      duration: 0.9,
      repeat: Infinity,
      ease: "easeInOut",
      delay,
    }}
  />
);

// A drifting flock. Crosses the map and wraps around.
const Flock = ({ y, from, to, duration, delay = 0 }) => (
  <motion.g
    animate={{ x: [from, to], opacity: [0, 0.75, 0.75, 0] }}
    transition={{
      duration,
      repeat: Infinity,
      ease: "linear",
      delay,
      times: [0, 0.12, 0.85, 1],
    }}
  >
    <g transform={`translate(0, ${y})`}>
      <Bird x={0} y={0} s={1} />
      <Bird x={20} y={9} s={0.75} delay={0.25} />
      <Bird x={40} y={-4} s={0.6} delay={0.45} />
    </g>
  </motion.g>
);

// Cloud built from stacked rectangles so every edge stays hard.
const CloudShape = () => (
  <g fill="#e2d2ba" stroke="#c4ac8d" strokeWidth="1">
    <rect x="0" y="10" width="66" height="12" />
    <rect x="12" y="2" width="26" height="10" />
    <rect x="34" y="5" width="20" height="7" />
  </g>
);

// A cloud drifting across the map, wrapping when it exits.
const DriftCloud = ({ y, from, to, duration, delay = 0, scale = 1, opacity = 0.5 }) => (
  <motion.g
    animate={{ x: [from, to] }}
    transition={{ duration, repeat: Infinity, ease: "linear", delay }}
    opacity={opacity}
  >
    <g transform={`translate(0, ${y}) scale(${scale})`}>
      <CloudShape />
    </g>
  </motion.g>
);

// Full-height grid item. The sticky element has to be a CHILD of this, not
// this element itself — a sticky grid item can only travel within its own
// (collapsed) area.
const Holder = styled.div`
  height: 100%;

  /* The map is decorative; below the two-column breakpoint the cards carry
     the whole section on their own. */
  @media (max-width: 1024px) {
    display: none;
  }
`;

const Sticky = styled.div`
  position: sticky;
  top: 100px;

  svg {
    width: 100%;
    height: auto;
    display: block;
    /* Keep the whole route on screen while pinned. */
    max-height: calc(100vh - 130px);
  }
`;

const VIEW_W = 420;
const NODE_R = 21;
const TOP_PAD = 96;
// Spacing is tuned so the whole route fits the pinned viewport without the
// svg's max-height kicking in and shrinking the captions to nothing. Raise it
// if stops are removed, lower it if more are added.
const GAP = 142;
// Just enough room under the final node for its two caption lines plus the
// date beneath them. 70 clipped the date's descenders once the trail shrank.
const BOTTOM_PAD = 84;

// How far short of the final node the plane parks, in svg px. Kept as a
// length rather than a percentage of the route: a percentage shrinks as
// stops are removed, which is how dropping to three stops left the plane
// sitting on top of the last circle instead of just above it.
const LANDING_GAP = 30;

// Hill centres as [x, fraction of total height for the base].
const HILLS = [
  [76, 0.3],
  [340, 0.51],
  [96, 0.79],
  [346, 0.95],
];

// Serpentine x-offsets, cycled so consecutive stops alternate sides.
const XS = [140, 250, 130, 260, 150];

// Company names run full length now, wrapped onto up to two lines so long ones
// ("Pravinya Information Technology Services") stay inside the column.
const wrap = (name, perLine = 20) => {
  const words = name.split(" ");
  const lines = [];
  let line = "";
  for (const w of words) {
    if (!line) line = w;
    else if ((line + " " + w).length <= perLine) line += " " + w;
    else {
      lines.push(line);
      line = w;
    }
  }
  if (line) lines.push(line);
  return lines;
};

const Trail = ({ progress }) => {
  const stops = experiences.map((e, i) => ({
    ...e,
    x: XS[i % XS.length],
    y: TOP_PAD + i * GAP,
  }));

  const height = TOP_PAD + (stops.length - 1) * GAP + BOTTOM_PAD;

  // Dashed path threading every campsite.
  const d = stops
    .map((s, i) => {
      if (i === 0) return `M ${s.x} ${s.y}`;
      const prev = stops[i - 1];
      const midY = (prev.y + s.y) / 2;
      return `C ${prev.x} ${midY}, ${s.x} ${midY}, ${s.x} ${s.y}`;
    })
    .join(" ");

  // The plane is flown by scroll position rather than a timed animation, so
  // one destination lines up with one experience card as you read down.
  const flown = useSpring(progress, {
    stiffness: 90,
    damping: 26,
    restDelta: 0.0005,
  });
  // Measured off a detached path so LANDING_GAP can be expressed in px.
  // offset-distance takes a length, but useTransform interpolates between two
  // values of the same unit, so the stop is converted to a percentage here.
  const endPercent = useMemo(() => {
    try {
      const p = document.createElementNS("http://www.w3.org/2000/svg", "path");
      p.setAttribute("d", d);
      const total = p.getTotalLength();
      return total ? ((total - LANDING_GAP) / total) * 100 : 95;
    } catch {
      return 95;
    }
  }, [d]);

  // Lands at 85% of the column's scroll rather than 100%. The map unpins soon
  // after the column bottom reaches the viewport bottom, so a flight spanning
  // the full range finished just as the map was sliding away and the arrival
  // never read as one. Ending early parks the plane while the map is still
  // pinned; useTransform clamps past its input range, so it holds there.
  const distance = useTransform(flown, [0, 0.85], ["4%", `${endPercent}%`]);


  return (
    <Holder aria-hidden="true">
      <Sticky>
      {/* The route draws itself on, then each destination lands in sequence. */}
      <motion.svg
        viewBox={`0 0 ${VIEW_W} ${height}`}
        role="presentation"
        variants={staggerContainer(0.16, 0.5)}
        initial="hidden"
        whileInView="show"
        viewport={viewport}
      >
        {/* Sky: clouds drift and flocks cross, behind everything else. The
            loops run continuously; MotionConfig neutralises them for anyone
            who prefers reduced motion. */}
        <g>
          <DriftCloud y={48} from={-90} to={VIEW_W + 20} duration={38} scale={1.1} opacity={0.75} />
          <DriftCloud y={222} from={VIEW_W + 40} to={-100} duration={52} scale={0.8} opacity={0.6} delay={5} />
          <DriftCloud y={388} from={-90} to={VIEW_W + 20} duration={45} scale={1.25} opacity={0.7} delay={12} />
          <DriftCloud y={556} from={VIEW_W + 40} to={-100} duration={60} scale={0.9} opacity={0.55} delay={3} />

          <Flock y={height * 0.24} from={-60} to={VIEW_W + 40} duration={22} />
          <Flock y={height * 0.56} from={VIEW_W + 40} to={-60} duration={28} delay={7} />
          <Flock y={height * 0.85} from={-60} to={VIEW_W + 40} duration={25} delay={14} />
        </g>

        {/* Terrain: flat triangles standing in for the objective's hills.
            Placed as a fraction of the route's height so removing a stop
            shortens the canvas without slicing a hill in half. */}
        <g fill="rgba(58, 42, 26, 0.13)">
          {HILLS.map(([cx, f], i) => {
            const base = height * f;
            return (
              <polygon
                key={i}
                points={`${cx - 36},${base} ${cx},${base - 60} ${cx + 36},${base}`}
              />
            );
          })}
        </g>

        {/* The trail reveals top-to-bottom behind a growing clip rect.
            framer-motion's `pathLength` cannot be used here: it drives the
            animation THROUGH strokeDasharray, which would overwrite the 7/7
            dash pattern and leave the trail rendering as a solid line. */}
        <defs>
          <clipPath id="trail-clip">
            <motion.rect
              x="0"
              y="0"
              width={VIEW_W}
              height={height}
              style={{ transformOrigin: "0px 0px" }}
              variants={trailWipe}
            />
          </clipPath>
        </defs>

        <path
          d={d}
          fill="none"
          stroke="#6c5b47"
          strokeWidth="2"
          strokeDasharray="7 7"
          strokeLinecap="butt"
          clipPath="url(#trail-clip)"
        />

        {stops.map((s) => (
          <motion.g key={s.id} variants={sealIn} style={POP}>
            {/* The open-ended present is the "what's next" X rather than a
                closed campsite circle — it is a destination on the route, not
                a separate marker floating beside it. */}
            {s.marker === "x" ? (
              <path
                d={`M ${s.x - 13} ${s.y - 13} L ${s.x + 13} ${s.y + 13}
                    M ${s.x + 13} ${s.y - 13} L ${s.x - 13} ${s.y + 13}`}
                stroke="#c0392b"
                strokeWidth="4.5"
                strokeLinecap="butt"
              />
            ) : (
              <circle
                cx={s.x}
                cy={s.y}
                r={NODE_R}
                fill="#3a2a1a"
                stroke={s.current ? "#ff8236" : "#000000"}
                strokeWidth={s.current ? 3 : 1}
              />
            )}
            {s.marker !== "x" && (
              <text
                x={s.x}
                y={s.y + 6}
                textAnchor="middle"
                fill="#f8eee1"
                fontSize="16"
                fontWeight="700"
                fontFamily="'Arial Black', Helvetica, sans-serif"
              >
                {s.company.charAt(0)}
              </text>
            )}

            {/* Full company name, wrapped rather than truncated. */}
            {wrap(s.company).map((line, li) => (
              <text
                key={li}
                x={s.x}
                y={s.y + NODE_R + 20 + li * 14}
                textAnchor="middle"
                fill="#3a2a1a"
                fontSize="12"
                fontWeight="700"
                fontFamily="Helvetica, Arial, sans-serif"
              >
                {line}
              </text>
            ))}
            <text
              x={s.x}
              y={s.y + NODE_R + 20 + wrap(s.company).length * 14 + 2}
              textAnchor="middle"
              fill="#6c5b47"
              fontSize="11"
              fontFamily="Helvetica, Arial, sans-serif"
            >
              {s.date}
            </text>

            {s.current && (
              <text
                x={s.x}
                y={s.y + NODE_R + 20 + wrap(s.company).length * 14 + 19}
                textAnchor="middle"
                fill="#8a5a2a"
                fontSize="11"
                fontWeight="700"
                fontStyle="italic"
                fontFamily="Helvetica, Arial, sans-serif"
              >
                You are here
              </text>
            )}
          </motion.g>
        ))}

        {/* The plane flies the route from the newest stop toward the oldest.
            Declared last so it passes over the nodes rather than under them,
            and stops short of the final one so both stay readable. */}
        <motion.g
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={viewport}
          transition={{ duration: 0.4, delay: 0.8 }}
          style={{
            offsetPath: `path("${d}")`,
            offsetRotate: "auto",
            offsetDistance: distance,
          }}
        >
          <Plane />
        </motion.g>
      </motion.svg>
      </Sticky>
    </Holder>
  );
};

export default Trail;
